/**
 * Where this driver has just been: a rolling trail of the last 500 km.
 *
 * The server copy belongs to the authenticated user, so their phone and head
 * unit see the same driven-road history while another driver sees none of it.
 * A per-user local cache keeps drawing and recording offline. New points are
 * uploaded as small, retry-safe batches instead of replacing the entire
 * 500 km record on every save.
 */

import type { Fix, LngLat } from '../core/types';
import { distance } from '../core/geo';
import {
  appendBreadcrumbBatch,
  fetchBreadcrumbs,
  type BreadcrumbBatch,
} from '../auth/api';

/** Total trail length kept, metres. The server enforces the same cap. */
const MAX_METRES = 500_000;
/** Don't record a new dot closer than this to the last one. */
const MIN_STEP_M = 30;
/** A jump further than this starts a new run instead of joining the line. */
const GAP_M = 1500;
/** A fix this inaccurate is not evidence of where the vehicle went. */
const MAX_ACCURACY_M = 60;
/** Persist and attempt sync at most this often while driving. */
const SAVE_EVERY_MS = 30_000;
/** Legacy 100 km cache, migrated once into the first signed-in user. */
const LEGACY_KEY = 'wayfarer.breadcrumbs.v1';
const BATCH_POINTS_MAX = 200;

interface Stored {
  runs: Array<Array<[number, number]>>;
  pending: BreadcrumbBatch[];
  revision: number;
}

export class BreadcrumbTrail {
  private readonly key: string;
  private runs: LngLat[][] = [];
  /** Metres in each run, kept alongside so trimming needn't re-measure. */
  private runMetres: number[] = [];
  private total = 0;
  private pending: BreadcrumbBatch[] = [];
  private current: BreadcrumbBatch | null = null;
  private continuingRun = false;
  private revision = 0;
  private lastSavedAt = Date.now();
  private dirty = false;
  private syncing = false;

  constructor(
    userId: number,
    private readonly changed: () => void = () => undefined,
  ) {
    this.key = `wayfarer.breadcrumbs.user.${userId}.v2`;
    const loaded = this.load(this.key);
    if (!loaded) this.migrateLegacy();
    this.measure();
    this.trim();

    // The gap between app sessions is deliberately a new run even if the car
    // restarts in the same place; we have no fixes proving the road between.
    this.runs.push([]);
    this.runMetres.push(0);

    const flush = () => this.save(true);
    window.addEventListener('pagehide', flush);
    window.addEventListener('online', () => void this.reconcile());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
      else void this.reconcile();
    });

    this.persist();
    void this.reconcile();
  }

  /** Feed every GPS fix; the trail decides what is worth keeping. */
  add(fix: Fix): boolean {
    if (fix.accuracy > MAX_ACCURACY_M) return false;
    const here: LngLat = [round5(fix.lng), round5(fix.lat)];
    let run = this.runs[this.runs.length - 1];
    const last = run.length ? run[run.length - 1] : null;

    if (last) {
      const moved = distance(last, here);
      if (moved < MIN_STEP_M) return false;

      if (moved > GAP_M) {
        this.finishCurrent();
        this.continuingRun = false;
        run = [here];
        this.runs.push(run);
        this.runMetres.push(0);
      } else {
        run.push(here);
        this.runMetres[this.runMetres.length - 1] += moved;
        this.total += moved;
      }
    } else {
      run.push(here);
    }

    if (!this.current) {
      this.current = {
        id: batchId(),
        points: [],
        continueLast: this.continuingRun,
      };
    }
    this.current.points.push(here);
    this.trim();
    this.dirty = true;
    this.save(false);
    return true;
  }

  /** Polylines for drawing. Single-point runs are invisible; skip them. */
  lines(): LngLat[][] {
    return this.runs.filter((run) => run.length >= 2);
  }

  /** Drop the oldest ground until the trail is back inside its 500 km. */
  private trim(): void {
    while (this.total > MAX_METRES && this.runs.length) {
      const first = this.runs[0];
      if (first.length < 2) {
        this.total -= this.runMetres.shift() ?? 0;
        this.runs.shift();
        continue;
      }
      const step = distance(first[0], first[1]);
      first.shift();
      this.runMetres[0] -= step;
      this.total -= step;
    }
  }

  private save(force: boolean): void {
    if (!this.dirty) return;
    const now = Date.now();
    if (!force && now - this.lastSavedAt < SAVE_EVERY_MS) return;
    this.lastSavedAt = now;
    this.finishCurrent();
    this.dirty = false;
    this.persist();
    void this.reconcile();
  }

  private finishCurrent(): void {
    if (!this.current?.points.length) return;
    for (let offset = 0; offset < this.current.points.length; offset += BATCH_POINTS_MAX) {
      this.pending.push({
        id: offset === 0 ? this.current.id : batchId(),
        points: this.current.points.slice(offset, offset + BATCH_POINTS_MAX),
        continueLast: offset === 0 ? this.current.continueLast : true,
      });
    }
    this.current = null;
    this.continuingRun = true;
  }

  /**
   * Pull the authenticated user's server trail, layer unsent local batches on
   * top, then drain the queue. Stable batch ids make a timed-out retry safe.
   */
  private async reconcile(): Promise<void> {
    if (this.syncing || !navigator.onLine) return;
    this.syncing = true;
    try {
      const initial = await fetchBreadcrumbs();
      this.rebuild(initial.breadcrumbs.runs);
      this.revision = initial.breadcrumbs.revision;
      this.changed();

      while (this.pending.length) {
        const batch = this.pending[0];
        const saved = await appendBreadcrumbBatch(batch);
        if (this.pending[0]?.id === batch.id) this.pending.shift();
        this.revision = saved.breadcrumbs.revision;
        this.persist();
      }

      // A second read picks up server-side trimming and points uploaded by a
      // different signed-in device while this one was draining its queue.
      const final = await fetchBreadcrumbs();
      this.rebuild(final.breadcrumbs.runs);
      this.revision = final.breadcrumbs.revision;
      this.persist();
      this.changed();
    } catch {
      // Offline/server failure is expected on the road. The queue remains in
      // this user's local cache and the online/visibility hooks retry it.
    } finally {
      this.syncing = false;
    }
  }

  /** Replace stale cached server data, then replay everything not yet sent. */
  private rebuild(remoteRuns: LngLat[][]): void {
    this.runs = cleanRuns(remoteRuns);
    for (const batch of [...this.pending, ...(this.current ? [this.current] : [])]) {
      if (!batch.points.length) continue;
      if (batch.continueLast && this.runs.length) {
        this.runs[this.runs.length - 1].push(...batch.points);
      } else {
        this.runs.push(batch.points.map((point) => [...point] as LngLat));
      }
    }
    if (!this.current && !this.continuingRun
      && (!this.runs.length || this.runs[this.runs.length - 1].length)) {
      this.runs.push([]);
    }
    this.measure();
    this.trim();
  }

  private measure(): void {
    this.runMetres = [];
    this.total = 0;
    for (const run of this.runs) {
      let metres = 0;
      for (let i = 1; i < run.length; i++) metres += distance(run[i - 1], run[i]);
      this.runMetres.push(metres);
      this.total += metres;
    }
  }

  private load(key: string): boolean {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      const stored = JSON.parse(raw) as Stored;
      this.runs = cleanRuns(stored?.runs);
      this.pending = cleanBatches(stored?.pending);
      this.revision = Math.max(0, Number(stored?.revision) || 0);
      return true;
    } catch {
      return false;
    }
  }

  /** Bring the old device-wide 100 km trail into this authenticated account. */
  private migrateLegacy(): void {
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) return;
      const legacy = JSON.parse(raw) as { runs?: unknown };
      this.runs = cleanRuns(legacy?.runs);
      for (const run of this.runs) {
        for (let offset = 0; offset < run.length; offset += BATCH_POINTS_MAX) {
          this.pending.push({
            id: batchId(),
            points: run.slice(offset, offset + BATCH_POINTS_MAX),
            continueLast: offset > 0,
          });
        }
      }
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      /* Corrupt or blocked legacy storage - start this user's trail clean. */
    }
  }

  private persist(): void {
    try {
      const stored: Stored = {
        runs: this.runs.filter((run) => run.length > 0),
        pending: [
          ...this.pending,
          ...(this.current?.points.length ? [this.current] : []),
        ],
        revision: this.revision,
      };
      localStorage.setItem(this.key, JSON.stringify(stored));
    } catch {
      /* Quota/private mode: live drawing continues, only reload recovery is lost. */
    }
  }
}

function cleanRuns(raw: unknown): LngLat[][] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(Array.isArray)
    .map((run) => run
      .filter((point) => Array.isArray(point)
        && point.length >= 2
        && Number.isFinite(Number(point[0]))
        && Number.isFinite(Number(point[1])))
      .map((point) => [round5(Number(point[0])), round5(Number(point[1]))] as LngLat))
    .filter((run) => run.length > 0);
}

function cleanBatches(raw: unknown): BreadcrumbBatch[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((batch): BreadcrumbBatch[] => {
    if (!batch || typeof batch !== 'object') return [];
    const b = batch as Partial<BreadcrumbBatch>;
    const points = cleanRuns([b.points])[0] ?? [];
    if (!points.length || typeof b.id !== 'string') return [];
    return [{ id: b.id, points, continueLast: b.continueLast === true }];
  });
}

function batchId(): string {
  const random = crypto.getRandomValues(new Uint32Array(2));
  return `crumb-${Date.now().toString(36)}-${random[0].toString(36)}${random[1].toString(36)}`;
}

function round5(n: number): number {
  return Math.round(n * 1e5) / 1e5;
}
